import { JsonObject, JsonProperty } from "typescript-json-serializer";

@JsonObject()
export class AuthoringInfoConfiguration {
    /**
     * Indicates which classification method was used if a classed color or classed size renderer was generated using one of the Smart Mapping functions.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#classificationMethod)
     */
    @JsonProperty()
    declare classificationMethod: | "equal-interval"
    | "defined-interval"
    | "manual"
    | "natural-breaks"
    | "quantile"
    | "standard-deviation";
    /**
     * Indicates the color ramp was used to create the symbols for Unique Value or Class Breaks renderer for Imagery Layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#colorRamp)
     */
    // declare colorRamp?: ColorRampProperties;
    /**
     * Only applicable to [HeatmapRenderer](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-HeatmapRenderer.html) created with the [heatmap renderer creator](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-heatmap.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#fadeRatio)
     */
    @JsonProperty()
    declare fadeRatio?: number;
    /**
     * A numeric field used for generating a [relationship renderer](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-relationship.html) along with [field2](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#field2).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#field1)
     */
    // declare field1: AuthoringInfoField1;
    /**
     * A numeric field used for generating a [relationship renderer](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-relationship.html) along with [field1](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#field1).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#field2)
     */
    // declare field2: AuthoringInfoField2;
    /**
     * An array of string values representing field names used for creating a [predominance renderer](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-predominance.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#fields)
     */
    @JsonProperty()
    declare fields: string[];
    /**
     * Only applicable to flow renderers.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#flowTheme)
     */
    @JsonProperty()
    declare flowTheme?: "flow-line" | "wave-front";
    /**
     * The focus of a [relationship renderer](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-relationship.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#focus)
     */
    @JsonProperty()
    declare focus: string;
    /**
     * Indicates whether the renderer was created internally by the JS API's rendering engine for default [FeatureReductionCluster](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-FeatureReductionCluster.html) visualizations.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#isAutoGenerated)
     */
    @JsonProperty()
    declare isAutoGenerated?: boolean;
    /**
     * **Only applicable to renderer used in web scenes.** Indicates the unit used in real-world sizes.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#lengthUnit)
     */
    @JsonProperty()
    declare lengthUnit:
    | "inches"
    | "feet"
    | "yards"
    | "miles"
    | "nautical-miles"
    | "millimeters"
    | "centimeters"
    | "decimeters"
    | "meters"
    | "kilometers"
    | "decimal-degrees";
    /**
     * Indicates the value of the upper handle if a slider was used to generate the dot value for dot density renderer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#maxSliderValue)
     */
    @JsonProperty()
    declare maxSliderValue: number;
    /**
     * Indicates the value of the lower handle if a slider was used to generate the dot value for dot density renderer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#minSliderValue)
     */
    @JsonProperty()
    declare minSliderValue: number;
    /**
     * The number of classes used to classify each field of a [relationship renderer](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-relationship.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#numClasses)
     */
    @JsonProperty()
    declare numClasses: number;
    /**
     * Indicates the standard deviation interval for each stop in a classed color or classed size renderer that was generated using the `standard-deviation` classification method.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#standardDeviationInterval)
     */
    @JsonProperty()
    declare standardDeviationInterval: number;
    /**
     * Only for renderers of type `univariate-color-size` with an `above-and-below` [univariateTheme](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#univariateTheme).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#statistics)
     */
    // statistics?: AuthoringInfoStatistics;
    /**
     * Indicates the renderer type generated from one of the Smart Mapping functions.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#type)
     */
    @JsonProperty()
    declare type:
    | "class-breaks-size"
    | "class-breaks-color"
    | "classed-color"
    | "classed-size"
    | "flow"
    | "predominance"
    | "relationship"
    | "univariate-color-size";
    /**
     * Only applicable to [univariateColorSize](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-univariateColorSize.html) renderers with an `above-and-below` [univariateTheme](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#univariateTheme).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#univariateSymbolStyle)
     */
    @JsonProperty()
    declare univariateSymbolStyle?:
    | "caret"
    | "circle-caret"
    | "arrow"
    | "circle-arrow"
    | "plus-minus"
    | "circle-plus-minus"
    | "square"
    | "circle"
    | "triangle"
    | "happy-sad"
    | "thumb"
    | "custom";
    /**
     * Only applicable to [univariateColorSize](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-univariateColorSize.html) renderers.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#univariateTheme)
     */
    @JsonProperty()
    declare univariateTheme?: "high-to-low" | "above" | "below" | "above-and-below";
    /**
     * Contains authoring properties of visual variables generated from one of the Smart Mapping methods or sliders.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-AuthoringInfo.html#visualVariables)
     */
    // @JsonProperty()
    // visualVariables: AuthoringInfoVisualVariable[];

    constructor() {}
}
